import React, { useEffect, useRef, useState } from 'react';
import { usePortletStore } from './App';
import ReactDOM from 'react-dom';
import { useViewStateStore, ViewStateStoreApi } from './stores/viewState.store';
import { NotificationList } from './components/NotificationList/NotificationList';
import { useNotificationStore } from './stores/notification.store';
import {
  Breakpoints,
  Button,
  Checkbox,
  FocusTrap,
  Icon,
  intl,
  intlFmt,
  Link,
  Toggle,
  useBreakpoint,
  useZoomBreakpoint
} from 'ehs-react';
import type { icModel } from 'generated-ts-common';
import { PopupMenu } from './components/PopupMenu/PopupMenu';
import { getNotificationTitle } from './utils/utils';

type NotificationSearchParameters = icModel.saf.infrastructure.model.notification.NotificationSearchParameters;

export function getRightMenuContainer(
  _isMobile: boolean,
  isMobileDeferred: boolean
): HTMLElement | null {
  let rightMenuContainer = document.getElementById(
    'additionalNaviContentRight'
  );

  const dashboardHeader = document.getElementById(
    'notification-center-hook-btn'
  );

  if (isMobileDeferred && dashboardHeader) {
    rightMenuContainer = dashboardHeader;
  }

  return rightMenuContainer;
}

export function MainView(): React.JSX.Element {
  const [userRole] = usePortletStore(store => [store.configuration.userRole]);
  const isMobile = useBreakpoint(Breakpoints.MOBILE);
  const [rightMenuContainer, setRightMenuContainer] = useState(() =>
    getRightMenuContainer(isMobile, isMobile)
  );

  useEffect(() => {
    const updateContainer = () =>
      setRightMenuContainer(getRightMenuContainer(isMobile, isMobile));
    // Dashboard's portal target may appear after its personal data loads.
    const observer = new MutationObserver(updateContainer);
    observer.observe(document.body, { childList: true, subtree: true });
    const timeout = setTimeout(updateContainer, 50);
    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, [isMobile]);

  return (
    <>
      {userRole && (
        <>
          <NaviButton rightMenuContainer={rightMenuContainer} />
          <NotificationCenter rightMenuContainer={rightMenuContainer} />
        </>
      )}
    </>
  );
}

interface MenuContainerProps {
  rightMenuContainer: HTMLElement | null;
}

function NotificationCenter({
  rightMenuContainer
}: Readonly<MenuContainerProps>): React.JSX.Element {
  const [open, toggle, setOpen] = useViewStateStore(store => [
    store.open,
    store.toggle,
    store.setOpen
  ]);
  const poeInstance = usePortletStore(store => store.poeInstance);
  const setOverlayOpenRef = useRef<((isOpen: boolean) => void) | undefined>(
    undefined
  );

  useEffect(() => {
    poeInstance.addEventListener('openNotificationCenter', eventParam => {
      const previousCallback = setOverlayOpenRef.current;
      if (previousCallback && previousCallback !== eventParam.setOverlayOpen) {
        previousCallback(false);
      }
      setOverlayOpenRef.current = eventParam.setOverlayOpen;
      if (ViewStateStoreApi.getState().open) {
        eventParam.setOverlayOpen(true);
      }
      setOpen(true);
    });
    return () => {
      poeInstance.removeEventListener('openNotificationCenter');
      setOverlayOpenRef.current?.(false);
      setOverlayOpenRef.current = undefined;
    };
  }, [poeInstance, setOpen]);

  useEffect(() => {
    setOverlayOpenRef.current?.(open);
    if (!open) {
      setOverlayOpenRef.current = undefined;
    }
  }, [open]);
  const [userRole] = usePortletStore(store => [store.configuration.userRole]);
  const [
    markAllAsRead,
    filter,
    updateFilter,
    checkForNewNotifications,
    numberOfUnreadNotifications
  ] = useNotificationStore(store => [
    store.markAllAsRead,
    store.filter,
    store.updateFilter,
    store.checkForNewNotifications,
    store.numberOfUnreadNotifications
  ]);

  const showOnlyUnread = filter.readStatus === false;

  useEffect(() => {
    const toId = setInterval(checkForNewNotifications, 60000);
    return () => clearInterval(toId);
  }, [checkForNewNotifications]);

  const mobileBp = useBreakpoint(Breakpoints.MOBILE);
  const mobileView = mobileBp && userRole === 'PATIENT';

  const notificationContainer = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      const bellButton = document.querySelector(
        '.notification-center-navi-button'
      );
      const confirmModal = document.querySelector('.ehs-modal');
      if (
        notificationContainer.current &&
        !notificationContainer.current.contains(event.target as Node) &&
        !bellButton?.contains(event.target as Node) &&
        !confirmModal?.contains(event.target as Node) &&
        open
      ) {
        toggle();
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [open, toggle]);
  const isZoomed = useZoomBreakpoint(1.3);
  if (rightMenuContainer) {
    return ReactDOM.createPortal(
      <div
        ref={notificationContainer}
        className={`notification-center-container${
          mobileView ? ' nc-mobile-view' : ''
        }${open && mobileView ? ' mobile-open' : ''}${
          !open ? ' nc-hidden' : ''
        }`}
        aria-hidden={!open}
        data-testid="notification-center-container"
        tabIndex={open ? 0 : -1}
      >
        <FocusTrap
          active={open}
          focusTrapOptions={{
            initialFocus: mobileView ? undefined : false,
            delayInitialFocus: !mobileView,
            fallbackFocus: () => notificationContainer.current!,
            clickOutsideDeactivates: true,
            returnFocusOnDeactivate: true
          }}
        >
          <div
            className={`notification-center-${userRole} ${
              open ? 'notification-center-open' : ''
            }`}
          >
            <div
              className={`notification-center-header${
                isZoomed ? ' zoomed' : ''
              }`}
            >
              {mobileView && (
                <div className="left">
                  <Button
                    data-testid="close-notification-center-button"
                    className="nc-icon-button"
                    variant="gray"
                    text={intl('view.button.close')}
                    icon="arrow-left"
                    onClick={toggle}
                  />
                </div>
              )}
              <h2>{intl('notificationHook.view.title')}</h2>
              <div className="right">
                {userRole === 'PATIENT' && (
                  <span
                    role="none"
                    onClick={() =>
                      sessionStorage.setItem(
                        'ASP::selectedTab',
                        'NOTIFICATIONS'
                      )
                    }
                  >
                    <Link
                      data-testid="notification-settings-button"
                      className="nc-icon-button icon-button"
                      aria-label={intl(
                        'notificationCenter.notificationSettings'
                      )}
                      href={
                        window.location.origin +
                        '/group/patient-portal/account-settings'
                      }
                      variant="ios"
                    >
                      <Icon
                        aria-hidden="true"
                        iconName="shui_settings_s"
                        aria-label={undefined}
                      ></Icon>
                    </Link>
                  </span>
                )}
                {!mobileView && (
                  <Button
                    data-testid="close-notification-center-button"
                    className="nc-icon-button"
                    variant="gray"
                    icon="shui-cancel"
                    text={intl('view.button.close')}
                    onClick={toggle}
                  />
                )}
              </div>
            </div>
            {userRole === 'PHYSICIAN' && (
              <Toggle
                data-testid="show-only-unread-toggle"
                value={showOnlyUnread}
                onChange={val =>
                  updateFilter({
                    ...filter,
                    readStatus: val ? false : undefined
                  })
                }
                label={intl('notificationCenter.filter.showOnlyUnread')}
              />
            )}
            <div className={'nc-actions'}>
              {numberOfUnreadNotifications > 0 && (
                <Button
                  data-testid="mark-all-as-read-button"
                  className="mark-all-as-read-button"
                  variant="gray"
                  text={intl('notificationHook.view.markAll')}
                  onClick={() => markAllAsRead()}
                />
              )}
              {numberOfUnreadNotifications <= 0 && <div />}
              {userRole === 'PHYSICIAN' && (
                <div className="right">
                  <PopupMenu
                    icon="shui_filter"
                    className="filter-popup"
                    arialLabel={intl('view.button.filter')}
                    dataTestId="notification-filter-button"
                  >
                    <NotificationFilter />
                  </PopupMenu>
                  <span
                    role="none"
                    onClick={() =>
                      sessionStorage.setItem(
                        'ASP::selectedTab',
                        'NOTIFICATIONS'
                      )
                    }
                  >
                    <Link
                      data-testid="notification-settings-button"
                      className="nc-icon-button icon-button"
                      aria-label={intl(
                        'notificationCenter.notificationSettings'
                      )}
                      href={
                        window.location.origin +
                        '/group/physician-portal/account-settings'
                      }
                      variant="ios"
                    >
                      <Icon
                        aria-hidden="true"
                        iconName="shui_settings_s"
                        aria-label={undefined}
                      ></Icon>
                    </Link>
                  </span>
                </div>
              )}
            </div>
            <NotificationList />
          </div>
        </FocusTrap>
      </div>,
      mobileView ? document.body : rightMenuContainer
    );
  } else {
    return <></>;
  }
}

function NaviButton({
  rightMenuContainer
}: Readonly<MenuContainerProps>): React.JSX.Element {
  const [open, toggle] = useViewStateStore(store => [store.open, store.toggle]);
  const [
    numberOfUnreadNotifications,
    getNumberOfUnreadNotifications
  ] = useNotificationStore(store => [
    store.numberOfUnreadNotifications,
    store.getNumberOfUnreadNotifications
  ]);

  useEffect(() => {
    getNumberOfUnreadNotifications();
  }, [getNumberOfUnreadNotifications]);

  if (rightMenuContainer) {
    return ReactDOM.createPortal(
      <div
        className={
          'notification-center-navi-button' +
          (open ? ' notification-center-navi-button-open' : '')
        }
      >
        <Button
          data-testid="toggle-notification-center-button"
          className="notification-center-button"
          variant="gray"
          text={
            intl('notificationCenter.header') +
            ' - ' +
            intlFmt(
              'notificationCenter.numberOfUnreadNotifications',
              numberOfUnreadNotifications + ''
            )
          }
          icon="bell"
          onClick={() => {
            if (rightMenuContainer.id !== 'notification-center-hook-btn') {
              toggle();
            }
          }}
        />
        {numberOfUnreadNotifications > 0 && (
          <div
            className="nc-unread-marker"
            data-testid="nc-unread-marker"
            role="presentation"
          >
            {numberOfUnreadNotifications <= 9
              ? numberOfUnreadNotifications
              : '9+'}
          </div>
        )}
      </div>,
      rightMenuContainer
    );
  } else {
    return <></>;
  }
}

function NotificationFilter(): React.JSX.Element {
  const [availableTopicTypes] = usePortletStore(store => [
    store.configuration.availableTopicTypes
  ]);
  const [filter, updateFilter] = useNotificationStore(store => [
    store.filter,
    store.updateFilter
  ]);

  return (
    <div className="notification-filter">
      {availableTopicTypes.map(topic => (
        <Checkbox
          key={'notification-filter-' + topic}
          label={getNotificationTitle(topic)}
          value={filter?.topicType?.split(',')?.includes(topic)}
          onChange={state => {
            let selTopics = [];
            if (state) {
              selTopics = [...(filter?.topicType?.split(',') ?? []), topic];
            } else {
              selTopics = [
                ...(filter?.topicType?.split(',') ?? []).filter(
                  t => t !== topic
                )
              ];
            }
            const updatedFilter: Partial<NotificationSearchParameters> = {
              topicType:
                selTopics.length <= 0 ? '---none---' : selTopics.join(',')
            };
            updateFilter(updatedFilter);
          }}
        />
      ))}
    </div>
  );
}
